/**
 * The typed fetch client. Every request in apps/app goes through `api.*`.
 *
 *  - Same-origin `/api/...` with the session cookie (`credentials: 'include'`).
 *  - JSON in and out; FormData / Blob bodies pass through untouched.
 *  - Every call accepts an AbortSignal. An aborted call rethrows the original
 *    AbortError (cancellation is control flow, not a failure).
 *  - Failures become `ApiError` with a user-safe message (see errors.ts).
 *  - A 401 clears the session hint and does a FULL page load of
 *    /login?redirect=<here>, unless the request opts out with
 *    `skipAuthRedirect` or the current page is a public auth page.
 *  - On an admin host (src/lib/surface.ts) every request carries
 *    `X-Lemon-Admin-Request: 1` (parity §0); the customer host never does.
 *  - While the tab explores sample data (src/lib/sample.ts) every request
 *    carries `X-Lemon-Sample: 1`; the API decides what that changes.
 */
import { clearSessionHint, isPublicAuthPath, loginPathFor } from '@/auth/session';
import { SAMPLE_HEADER, isSampleActive } from '@/lib/sample';
import { ADMIN_REQUEST_HEADER, isAdminSurface } from '@/lib/surface';
import { ApiError, NETWORK_MESSAGE, TIMEOUT_MESSAGE, apiErrorFromResponse, isAbortError } from './errors';

export type QueryPrimitive = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryPrimitive | readonly QueryPrimitive[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Query string. Arrays repeat the key (`?os=ios&os=android`), as FastAPI expects. null/undefined are dropped. */
  query?: QueryParams;
  /** JSON-serialised unless it is FormData, Blob, URLSearchParams or a string. */
  body?: unknown;
  signal?: AbortSignal;
  /** Fail with a timeout ApiError after this many milliseconds. */
  timeoutMs?: number;
  /** Treat 401 as an answer (session probes, sign-in) rather than redirecting. */
  skipAuthRedirect?: boolean;
  headers?: Record<string, string>;
}

export const buildUrl = (path: string, query?: QueryParams): string => {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    const values = Array.isArray(raw) ? raw : [raw];
    for (const value of values as QueryPrimitive[]) {
      if (value === null || value === undefined) continue;
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
};

const isRawBody = (body: unknown): body is BodyInit =>
  body instanceof FormData ||
  body instanceof Blob ||
  body instanceof URLSearchParams ||
  typeof body === 'string';

const parseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  const type = response.headers.get('content-type') ?? '';
  if (!type.includes('json')) return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

/** Swappable so tests can observe the redirect without navigating jsdom. */
export const authRedirect = {
  go: (url: string): void => {
    window.location.assign(url);
  },
};

const redirectToLogin = (): void => {
  const { pathname, search, hash } = window.location;
  authRedirect.go(loginPathFor(`${pathname}${search}${hash}`));
};

/**
 * The transport: builds the request, maps network failures, timeouts and
 * non-2xx responses to ApiError (with the 401 redirect), and returns the OK
 * response for the caller to read.
 */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', query, body, signal, timeoutMs, skipAuthRedirect = false } = options;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(isAdminSurface() ? { [ADMIN_REQUEST_HEADER]: '1' } : {}),
    ...(isSampleActive() ? { [SAMPLE_HEADER]: '1' } : {}),
    ...options.headers,
  };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (isRawBody(body)) {
      payload = body;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  const timeoutSignal = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
  const signals = [signal, timeoutSignal].filter((s): s is AbortSignal => s !== undefined);
  const combined = signals.length > 1 ? AbortSignal.any(signals) : signals[0];

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: payload ?? null,
      credentials: 'include',
      signal: combined ?? null,
    });
  } catch (error) {
    if (timeoutSignal?.aborted && !signal?.aborted) {
      throw new ApiError({ message: TIMEOUT_MESSAGE, status: 0, kind: 'timeout' });
    }
    if (isAbortError(error)) throw error;
    throw new ApiError({ message: NETWORK_MESSAGE, status: 0, kind: 'network', detail: error });
  }

  if (response.ok) return response;

  const apiError = apiErrorFromResponse(response.status, await parseBody(response));
  if (response.status === 401) {
    clearSessionHint();
    if (!skipAuthRedirect && !isPublicAuthPath(window.location.pathname)) redirectToLogin();
  } else if (apiError.shouldLog) {
    console.error('API error', { path, status: apiError.status, code: apiError.code, message: apiError.message });
  }
  throw apiError;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  return (await parseBody(response)) as T;
}

/** A file served by the API, with the name the server gave it (Content-Disposition). */
export interface Download {
  blob: Blob;
  /** From `Content-Disposition` (`filename*=` preferred); null when the server sent none. */
  filename: string | null;
}

export const filenameFromContentDisposition = (header: string | null): string | null => {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain parameter */
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const name = (plain?.[2] ?? plain?.[1])?.trim();
  return name ? name : null;
};

/** GET a binary response (exports). Same errors, aborts and 401 handling as `api.get`. */
export async function apiDownload(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<Download> {
  const response = await send(path, { ...options, method: 'GET', headers: { Accept: '*/*', ...options.headers } });
  return { blob: await response.blob(), filename: filenameFromContentDisposition(response.headers.get('content-disposition')) };
}

type Opts = Omit<RequestOptions, 'method' | 'body'>;

export const api = {
  get: <T>(path: string, options?: Opts) => apiRequest<T>(path, { ...options, method: 'GET' }),
  delete: <T = undefined>(path: string, options?: Opts) => apiRequest<T>(path, { ...options, method: 'DELETE' }),
  post: <T>(path: string, body?: unknown, options?: Opts) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Opts) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Opts) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  download: (path: string, options?: Opts) => apiDownload(path, options),
};
